import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Closes a protected view after a period of keyboard / mouse inactivity.
 *
 * Used to satisfy the PII security requirement that an attachment view must
 * auto-close when the user walks away. Any of mousemove / mousedown / click /
 * keydown / touchstart / wheel / scroll resets the timer.
 *
 * @param {boolean} active  Whether the timer should be running.
 * @param {Function} onTimeout  Called once when inactivity expires.
 * @param {number} timeoutMs  Inactivity window in milliseconds.
 * @returns {{ secondsRemaining: number, reset: Function }}
 */
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "click", "keydown", "touchstart", "wheel", "scroll"];

export default function useInactivityTimeout(active, onTimeout, timeoutMs = 60000) {
    const timerRef = useRef(null);
    const tickRef = useRef(null);
    const onTimeoutRef = useRef(onTimeout);
    const deadlineRef = useRef(0);
    const [secondsRemaining, setSecondsRemaining] = useState(Math.ceil(timeoutMs / 1000));

    useEffect(() => {
        onTimeoutRef.current = onTimeout;
    }, [onTimeout]);

    const clearTimers = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    }, []);

    const reset = useCallback(() => {
        if (!active) return;
        clearTimers();
        deadlineRef.current = performance.now() + timeoutMs;
        setSecondsRemaining(Math.ceil(timeoutMs / 1000));

        timerRef.current = setTimeout(() => {
            onTimeoutRef.current?.();
        }, timeoutMs);

        tickRef.current = setInterval(() => {
            const remaining = Math.max(0, Math.ceil((deadlineRef.current - performance.now()) / 1000));
            setSecondsRemaining(remaining);
        }, 1000);
    }, [active, clearTimers, timeoutMs]);

    useEffect(() => {
        if (!active) {
            clearTimers();
            return undefined;
        }

        reset();

        const handleActivity = () => reset();
        const options = { passive: true, capture: true };
        ACTIVITY_EVENTS.forEach((evt) => document.addEventListener(evt, handleActivity, options));

        return () => {
            ACTIVITY_EVENTS.forEach((evt) => document.removeEventListener(evt, handleActivity, options));
            clearTimers();
        };
    }, [active, reset, clearTimers]);

    return { secondsRemaining, reset };
}
